import { DEFAULT_PAGE_SIZE } from "../config";
import { ImportResult } from "./import-result";
import { MappingEntry } from "./mapping";
import type { MyPayEnvironment } from "../../generated/core-api";

/**
 * Parsed representation of a CSV file for the preview table.
 * The server owns authoritative parsing; this shape is for display only.
 */
export interface CsvPreview {
  /** Trimmed column headers from the CSV. */
  headers: string[];
  /** Rows as key-value maps using the trimmed header names. */
  rows: Record<string, string>[];
  /** Distinct values from the detected component column, preserving first-seen order. */
  componentNames: string[];
  /**
   * The detected component-identifying header ("Component Name" or the legacy
   * "Deduction Name"), or null if none present. Used to default the component
   * column selector on the Upload screen.
   */
  componentColumn: string | null;
  /**
   * Header names that appeared more than once in the source file (before
   * disambiguation). Empty when every column name is unique.
   */
  duplicateHeaders: DuplicateHeaderGroup[];
}

/** One duplicated header name in the source CSV and how many columns share it. */
export interface DuplicateHeaderGroup {
  name: string;
  count: number;
}

/**
 * Wizard state held in `WizardContext`. All fields are optional so the context
 * can start empty and be progressively populated as the user moves through steps.
 */
export interface WizardState {
  /** The raw File object selected by the user (retained for re-sending on the map step). */
  file: File | null;
  /** Raw CSV text (read from `file` for the parser). */
  csvText: string | null;
  /** Parsed preview: headers, rows, and distinct component names. */
  preview: CsvPreview | null;
  /** Optional provider identifier (e.g. "xero"). Free-form text — no provider-list endpoint. */
  providerId: string | null;
  /**
   * Target MyPay API deployment for import/discovery calls: dry-run import
   * (`useImportPayComponents`), commit workflow invocations, and component-parameter
   * discovery (`useComponentsParameters` / `useComponentParameters`).
   */
  mypayEnvironment: MyPayEnvironment | null;
  /**
   * Optional override for which CSV column identifies the component. Defaults to
   * the detected "Component Name"/"Deduction Name" header; sent as `component_column`.
   */
  componentColumn: string | null;
  /**
   * Which CSV column supplies per-row start dates (session-only).
   * - `null` — not initialised yet (Map Columns pre-selects reserved `Start Date`
   *   when present)
   * - `""` — explicit none (skip start dates)
   * - otherwise — that header; sent as `start_date_column` on preflight
   */
  startDateColumn: string | null;
  /**
   * Which CSV column supplies per-row end dates (session-only).
   * - `null` — not initialised yet (Map Columns pre-selects reserved `End Date`
   *   when present)
   * - `""` — explicit none (skip end dates)
   * - otherwise — that header; sent as `end_date_column` on preflight
   */
  endDateColumn: string | null;
  /** The latest result returned by `POST /pay-components/import`. Drives which screen is shown. */
  importResult: ImportResult | null;
  /**
   * True when `importResult` no longer matches the current CSV / mappings /
   * component column / environment (redesign). Confirm import stays disabled
   * until the user re-validates.
   */
  validationStale: boolean;
  /**
   * 1-based CSV row indexes edited since the last successful validation
   * (redesign Review). Cleared when dry-run completes. Used to group those
   * rows separately until the user re-validates.
   */
  editedRowIndexes: number[];
  /** In-progress column→parameter mappings being edited on the Map Columns screen. */
  pendingMappings: MappingEntry[];
  /**
   * PS user's chosen instance per ambiguous component name (D4): component
   * (display) name → selected `companyComponentId`. Empty until the Map Columns
   * instance picker records a choice; consumed when building the wire rows so an
   * ambiguous name carries its disambiguating id.
   */
  componentResolutions: Record<string, string>;
  /** Rows shown per page across the wizard tables (seeded from LD, clamped to MAX_PAGE_SIZE). */
  pageSize: number;
  /**
   * Failed/skipped source rows (original CSV rows plus a Reason column) captured
   * at commit time, so the Results screen can offer a "Download errors" action
   * instead of auto-downloading. Null when the import had no failures.
   */
  failedRowsCsv: string | null;
}

export const INITIAL_WIZARD_STATE: WizardState = {
  file: null,
  csvText: null,
  preview: null,
  providerId: null,
  mypayEnvironment: null,
  componentColumn: null,
  startDateColumn: null,
  endDateColumn: null,
  importResult: null,
  validationStale: false,
  editedRowIndexes: [],
  pendingMappings: [],
  componentResolutions: {},
  pageSize: DEFAULT_PAGE_SIZE,
  failedRowsCsv: null,
};

/**
 * Reset wizard state for a fresh import while preserving session context chosen
 * in the company gate (`mypayEnvironment`) and the user's table page-size preference.
 *
 * Without preserving `mypayEnvironment`, the Upload screen keeps Continue disabled
 * after "Start a new import" because the layout gate does not re-seed wizard state.
 */
export function newImportWizardState(from: WizardState): WizardState {
  return {
    ...INITIAL_WIZARD_STATE,
    mypayEnvironment: from.mypayEnvironment,
    pageSize: from.pageSize,
  };
}
